import { Flex, Heading, Section, Text } from '@radix-ui/themes'
import { useMemo } from 'react'
import {
  DataImportFileType,
  DataImportStatus,
  DataImportTagResponse,
} from '@Generated/back-office-client/src'
import { convertEnumToDisplay, getEnumValues } from './utils'
import { Typeahead } from '@Components/common/typeahead'
import { TypeaheadOption } from './types'

type SearchFiltersSectionProps = {
  selectedFileTypes: DataImportFileType[] | undefined
  selectedStatus: DataImportStatus | undefined
  selectedTags: string[]
  importTags: DataImportTagResponse | undefined
  onFileTypeChange: (values: readonly TypeaheadOption[]) => void
  onStatusChange: (value: TypeaheadOption | null) => void
  onTagChange: (values: readonly TypeaheadOption[]) => void
}

export const SearchFiltersSection = ({
  selectedFileTypes,
  selectedStatus,
  selectedTags,
  importTags,
  onFileTypeChange,
  onStatusChange,
  onTagChange,
}: SearchFiltersSectionProps) => {
  const importTypeConfig = useMemo(() => {
    const options = getEnumValues(DataImportFileType)
      .map((type) => ({
        value: type,
        label: convertEnumToDisplay(type),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    const selectedOptions = options.filter((o) => selectedFileTypes?.includes(o.value))

    return { options, selectedOptions }
  }, [selectedFileTypes])

  const statusConfig = useMemo(() => {
    const options = getEnumValues(DataImportStatus)
      .map((type) => ({
        value: type,
        label: convertEnumToDisplay(type),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    const selectedOption = options.find((o) => selectedStatus === o.value)

    return { options, selectedOption }
  }, [selectedStatus])

  const tagsConfig = useMemo(() => {
    const options = importTags?.tags.map((tag) => ({ value: tag, label: tag })) ?? []

    const selectedOptions = options.filter((o) => selectedTags?.includes(o.value))

    return { options, selectedOptions }
  }, [importTags?.tags, selectedTags])

  return (
    <Section className="import-filter-container" size="2">
      <Heading>Search & Filter</Heading>
      <Flex justify="between" className="import-filter-inner-container" gap="6">
        <Flex direction="column" className="filter">
          <Text>Import Type</Text>
          <Typeahead
            isMulti
            isClearable
            onChange={onFileTypeChange}
            value={importTypeConfig.selectedOptions}
            options={importTypeConfig.options}
          />
        </Flex>
        <Flex direction="column" className="filter">
          <Text>Status</Text>
          <Typeahead
            isMulti={false}
            isClearable
            onChange={onStatusChange}
            value={statusConfig.selectedOption}
            options={statusConfig.options}
          />
        </Flex>
        <Flex direction="column" className="filter">
          <Text>Tags</Text>
          <Typeahead
            isMulti
            isClearable
            onChange={onTagChange}
            value={tagsConfig.selectedOptions}
            options={tagsConfig.options}
          />
        </Flex>
      </Flex>
    </Section>
  )
}
